import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './MainScreens.css';

const CUISINE_OPTIONS = [
  { id: 'italian', emoji: '🍝', label: 'Italian' },
  { id: 'mexican', emoji: '🌮', label: 'Mexican' },
  { id: 'chinese', emoji: '🥡', label: 'Chinese' },
  { id: 'japanese', emoji: '🍣', label: 'Japanese' },
  { id: 'indian', emoji: '🍛', label: 'Indian' },
  { id: 'thai', emoji: '🍜', label: 'Thai' },
  { id: 'american', emoji: '🍔', label: 'American' },
  { id: 'mediterranean', emoji: '🥙', label: 'Mediterranean' },
  { id: 'korean', emoji: '🥘', label: 'Korean' },
  { id: 'pizza', emoji: '🍕', label: 'Pizza' },
  { id: 'seafood', emoji: '🦐', label: 'Seafood' },
  { id: 'bbq', emoji: '🥩', label: 'BBQ' },
];

const MOOD_OPTIONS = [
  { value: 'quick', label: 'Quick Bite', emoji: '⚡' },
  { value: 'comfort', label: 'Comfort', emoji: '🍲' },
  { value: 'healthy', label: 'Healthy', emoji: '🥗' },
  { value: 'fancy', label: 'Date Night', emoji: '🍷' },
];

const PRICE_LEVELS = [
  { level: 1, label: '$', desc: 'Budget' },
  { level: 2, label: '$$', desc: 'Moderate' },
  { level: 3, label: '$$$', desc: 'Upscale' },
  { level: 4, label: '$$$$', desc: 'Fine dining' },
];

const DISTANCE_OPTIONS = [
  { value: 1609, label: '1 mi', desc: 'Walking' },
  { value: 4828, label: '3 mi', desc: 'Close' },
  { value: 8047, label: '5 mi', desc: 'Nearby' },
  { value: 16093, label: '10 mi', desc: 'Driving' },
  { value: 24140, label: '15 mi', desc: 'Far out' },
];

const COUNT_OPTIONS = [
  { value: 5, label: '5', desc: 'Quick' },
  { value: 8, label: '8', desc: 'Standard' },
  { value: 10, label: '10', desc: 'More' },
  { value: 15, label: '15', desc: 'Max' },
];

export default function PreferencesScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || 'solo';
  const joinSessionCode = location.state?.joinSessionCode || null;
  const source = location.state?.source || null; // 'favorites' when launched from favorites

  const [cuisines, setCuisines] = useState([]);
  const [priceRange, setPriceRange] = useState([1, 2, 3]);
  const [venueType, setVenueType] = useState('both');
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  const [mood, setMood] = useState(null);
  const [distance, setDistance] = useState(8047);
  const [resultCount, setResultCount] = useState(5);
  const [loading, setLoading] = useState(source === 'favorites');
  const [error, setError] = useState('');
  const [geoLocation, setGeoLocation] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [cuisineAvailability, setCuisineAvailability] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [searchUsage, setSearchUsage] = useState(null);
  const [upgradeModalData, setUpgradeModalData] = useState(null);

  useEffect(() => {
    // Load search usage for free user limit display
    api.get('/sessions/usage').then(res => setSearchUsage(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGeoLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          setGeoError('');
        },
        (err) => {
          console.warn('Geolocation error:', err.message);
          setGeoError('Location access denied. Results may be less accurate.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGeoError('Geolocation not supported by your browser.');
    }
  }, []);

  // Check cuisine availability when cuisines or location changes
  useEffect(() => {
    if (cuisines.length > 0 && geoLocation) {
      checkCuisineAvailability();
    }
  }, [cuisines, geoLocation, distance, venueType]);

  // Smart result count adjustment based on cuisine selection
  useEffect(() => {
    if (cuisines.length >= 3 && resultCount < 8) {
      setResultCount(8); // Auto-increase for many cuisines
    }
  }, [cuisines.length]);

  async function checkCuisineAvailability() {
    if (!geoLocation || cuisines.length === 0) return;
    
    setCheckingAvailability(true);
    try {
      const { data } = await api.post('/restaurants/check-availability', {
        latitude: geoLocation.latitude,
        longitude: geoLocation.longitude,
        cuisines,
        radius: distance,
        venueType,
      });
      setCuisineAvailability(data.availability || {});
    } catch (err) {
      console.error('Availability check error:', err);
    } finally {
      setCheckingAvailability(false);
    }
  }

  // Get time-based suggestions
  function getTimeBasedSuggestions() {
    const hour = new Date().getHours();
    if (hour < 11) {
      return { mood: null, cuisines: ['american'], label: '☀️ Breakfast time!' };
    } else if (hour >= 11 && hour < 14) {
      return { mood: 'quick', cuisines: [], label: '⚡ Quick lunch?' };
    } else if (hour >= 17 && hour < 21) {
      return { mood: null, cuisines: [], label: '🍽️ Dinner time!' };
    } else if (hour >= 21) {
      return { mood: null, cuisines: [], label: '🌙 Late night eats' };
    }
    return null;
  }

  function applyTimeBasedSuggestion() {
    const suggestion = getTimeBasedSuggestions();
    if (suggestion) {
      if (suggestion.mood) setMood(suggestion.mood);
      if (suggestion.cuisines.length > 0) setCuisines(suggestion.cuisines);
    }
  }


  // Auto-launch favorites session
  useEffect(() => {
    if (source === 'favorites') {
      startFavoritesSession();
    }
  }, [source]);

  async function startFavoritesSession() {
    try {
      setLoading(true);
      const { data: favData } = await api.get('/favorites/for-session');
      const restaurants = favData.restaurants || [];
      if (restaurants.length < 2) {
        setError('Need at least 2 favorites to swipe');
        setLoading(false);
        return;
      }
      const { data } = await api.post('/sessions/create', {
        mode: 'solo',
        maxParticipants: 1,
        restaurants,
        preferences: {
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude,
          source: 'favorites',
        },
      });
      navigate(`/session/${data.sessionCode}`, {
        state: { mode: 'solo', restaurants },
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start favorites session');
      setLoading(false);
    }
  }

  function toggleCuisine(id) {
    setCuisines(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  }

  function togglePrice(level) {
    setPriceRange(prev => prev.includes(level) ? prev.filter(p => p !== level) : [...prev, level].sort());
  }

  async function handleStart() {
    if (priceRange.length === 0) {
      setError('Select at least one price range');
      return;
    }
    setLoading(true);
    setError('');

    // Merge venueType + restaurantFilter into a single venueType for the backend
    let effectiveVenueType = venueType;
    if (restaurantFilter === 'local') effectiveVenueType = 'local';
    else if (restaurantFilter === 'chains') effectiveVenueType = 'chains';

    const myPrefs = {
      latitude: geoLocation?.latitude,
      longitude: geoLocation?.longitude,
      mood,
      cuisines,
      priceRange,
      venueType: effectiveVenueType,
    };

    try {
      // If joining an existing couple session (partner accepting invite)
      if (joinSessionCode) {
        const { data: prefData } = await api.post(`/sessions/${joinSessionCode}/preferences`, {
          preferences: myPrefs,
        });
        if (prefData.status === 'active') {
          navigate(`/session/${joinSessionCode}`, { replace: true });
          return;
        }
        navigate(`/waiting-room`, {
          state: {
            sessionCode: joinSessionCode,
            mode,
            isCreator: false,
            partnerAutoAdded: true,
            restaurants: prefData.restaurants || [],
          },
          replace: true,
        });
        return;
      }

      // COUPLE MODE (creator): Create session with prefs only, wait for partner
      if (mode === 'couple') {
        const { data } = await api.post('/sessions/create', {
          mode,
          maxParticipants: 2,
          restaurants: [],
          preferences: myPrefs,
        });

        if (data.redirectToExisting) {
          const { data: prefData } = await api.post(`/sessions/${data.sessionCode}/preferences`, {
            preferences: myPrefs,
          });
          if (prefData.status === 'active') {
            navigate(`/session/${data.sessionCode}`, { replace: true });
            return;
          }
          navigate(`/waiting-room`, {
            state: {
              sessionCode: data.sessionCode,
              mode,
              isCreator: false,
              partnerAutoAdded: true,
              restaurants: prefData.restaurants || [],
            },
            replace: true,
          });
          return;
        }

        const { data: creatorPrefData } = await api.post(`/sessions/${data.sessionCode}/preferences`, {
          preferences: myPrefs,
        });

        if (creatorPrefData.status === 'active') {
          navigate(`/session/${data.sessionCode}`, { replace: true });
          return;
        }

        navigate(`/waiting-room`, {
          state: {
            sessionCode: data.sessionCode,
            mode,
            isCreator: true,
            partnerAutoAdded: data.partnerAutoAdded || false,
            restaurants: [],
          },
          replace: true,
        });
        return;
      }

      // SOLO / GROUP MODE: Fetch restaurants immediately
      let restData;
      try {
        const response = await api.post('/restaurants/fetch', {
          latitude: geoLocation?.latitude,
          longitude: geoLocation?.longitude,
          cuisines,
          mood,
          priceRange,
          venueType: effectiveVenueType,
          radius: distance,
          limit: resultCount,
        });
        restData = response.data;
      } catch (fetchErr) {
        console.error('Restaurant fetch error:', fetchErr);
        setError('Failed to fetch restaurants. Please check your internet connection and try again.');
        setLoading(false);
        return;
      }

      // Dedupe by placeId (Google's unique identifier) to prevent same restaurant appearing twice
      const seen = new Set();
      const restaurants = (restData.restaurants || []).filter((r) => {
        // Use placeId if available (from Google), otherwise fall back to name-based deduplication
        const key = r.placeId || r.id || r.name?.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (restaurants.length === 0) {
        setError('No restaurants found — try broadening your preferences.');
        setLoading(false);
        return;
      }

      // Create session
      const { data } = await api.post('/sessions/create', {
        mode,
        maxParticipants: mode === 'solo' ? 1 : 10,
        restaurants,
        preferences: myPrefs,
      });

      if (mode === 'solo') {
        navigate(`/session/${data.sessionCode}`, { replace: true });
      } else {
        // Group mode — go to waiting room
        navigate(`/waiting-room`, {
          state: {
            sessionCode: data.sessionCode,
            mode,
            isCreator: true,
            partnerAutoAdded: false,
            restaurants,
          },
          replace: true,
        });
      }
    } catch (err) {
      console.error('Session creation error:', err);
      if (err.response?.status === 429 && err.response?.data?.limit_reached) {
        setError(null);
        setLoading(false);
        setUpgradeModalData(err.response.data);
        return;
      }
      setError(err.response?.data?.error || err.message || 'Failed to start session');
      setLoading(false);
    }
  }

  function handleSurpriseMe() {
    setVenueType('both');
    setRestaurantFilter('all');
    setMood(null);
    setCuisines([]);
    setPriceRange([1, 2, 3, 4]);
    setDistance(8047);
    setResultCount(10);
    setTimeout(() => handleStart(), 100);
  }

  return (
    <div className="main-screen">
      <MainHeader title="What sounds good?" showBack={true} backPath="/home" />

      {/* Free user search limit indicator */}
      {searchUsage && !searchUsage.is_premium && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          margin: '0 16px 0',
          background: searchUsage.searches_remaining > 0 ? '#1a2a1a' : '#3a1a1a',
          border: `1px solid ${searchUsage.searches_remaining > 0 ? '#2a4a2a' : '#FF6347'}`,
          borderRadius: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>{searchUsage.searches_remaining > 0 ? '🔍' : '🔒'}</span>
            <span style={{ color: '#ccc', fontSize: '13px' }}>
              {searchUsage.searches_remaining > 0
                ? `${searchUsage.searches_remaining} of ${searchUsage.searches_limit} free searches left this week`
                : 'No free searches left this week'}
            </span>
          </div>
          <button
            onClick={() => navigate('/subscription')}
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '700',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Go Unlimited
          </button>
        </div>
      )}

      <main className="screen-main">
        <div className="preferences-container">
          {geoError && (
            <div className="pref-warning">
              📍 {geoError}
            </div>
          )}

          {error && (
            <div className="pref-error">{error}</div>
          )}

          {getTimeBasedSuggestions() && (
            <div className="time-suggestion-banner" onClick={applyTimeBasedSuggestion}>
              <span className="time-icon">{getTimeBasedSuggestions().label}</span>
              <span className="time-action">Tap to apply</span>
            </div>
          )}

          <div className="pref-section">
            <h2 className="pref-title">🍽️ Type of Place</h2>
            <div className="venue-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {[
                { id: 'restaurant', emoji: '🍽️', label: 'Restaurants' },
                { id: 'fastfood', emoji: '🍟', label: 'Fast Food' },
                { id: 'both', emoji: '🔥', label: 'Both' },
              ].map(v => (
                <button
                  key={v.id}
                  className={`venue-btn ${venueType === v.id ? 'active' : ''}`}
                  onClick={() => setVenueType(v.id)}
                >
                  <span className="venue-emoji">{v.emoji}</span>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="venue-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {[
                { id: 'local', emoji: '📍', label: 'Local' },
                { id: 'chains', emoji: '🏢', label: 'Chains' },
                { id: 'all', emoji: '✨', label: 'All' },
              ].map(v => (
                <button
                  key={v.id}
                  className={`venue-btn ${restaurantFilter === v.id ? 'active' : ''}`}
                  onClick={() => setRestaurantFilter(v.id)}
                >
                  <span className="venue-emoji">{v.emoji}</span>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pref-section">
            <h2 className="pref-title">😋 Vibe (optional)</h2>
            <div className="mood-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {MOOD_OPTIONS.map(option => (
                <button
                  key={option.value}
                  className={`mood-btn ${mood === option.value ? 'active' : ''}`}
                  onClick={() => setMood(mood === option.value ? null : option.value)}
                >
                  <span>{option.emoji}</span> {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pref-section">
            <h2 className="pref-title">🍜 Cuisine (optional)</h2>
            <p className="pref-hint">Leave blank for all cuisines</p>
            <div className="cuisine-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {CUISINE_OPTIONS.map(c => {
                const isSelected = cuisines.includes(c.id);
                return (
                  <button
                    key={c.id}
                    className={`cuisine-btn ${isSelected ? 'active' : ''}`}
                    onClick={() => toggleCuisine(c.id)}
                  >
                    <span>{c.emoji}</span> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pref-section">
            <h2 className="pref-title">📏 Distance & Options</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
              {DISTANCE_OPTIONS.slice(1, 4).map(d => (
                <button
                  key={d.value}
                  className={`distance-btn ${distance === d.value ? 'active' : ''}`}
                  onClick={() => setDistance(d.value)}
                  style={{ padding: '12px 8px' }}
                >
                  <span className="distance-label" style={{ fontSize: '16px', fontWeight: 'bold' }}>{d.label}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {COUNT_OPTIONS.slice(0, 3).map(c => (
                <button
                  key={c.value}
                  className={`count-btn ${resultCount === c.value ? 'active' : ''}`}
                  onClick={() => setResultCount(c.value)}
                  style={{ padding: '12px 8px' }}
                >
                  <span className="count-label" style={{ fontSize: '16px', fontWeight: 'bold' }}>{c.value} spots</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pref-section">
            <h2 className="pref-title">💰 Price Range</h2>
            <div className="price-options">
              {PRICE_LEVELS.map(p => (
                <button
                  key={p.level}
                  className={`price-btn ${priceRange.includes(p.level) ? 'active' : ''}`}
                  onClick={() => togglePrice(p.level)}
                >
                  <span className="price-label">{p.label}</span>
                  <span className="price-desc">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pref-actions">
            <button
              className="btn btn-secondary btn-lg"
              onClick={handleSurpriseMe}
              disabled={loading}
            >
              🎲 Surprise Me
            </button>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleStart}
              disabled={loading}
            >
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner-small"></span> Finding food...
                </span>
              ) : (
                'Find Food 🔥'
              )}
            </button>
          </div>
        </div>
      </main>

      {upgradeModalData && (
        <>
          <div className="modal-overlay" onClick={() => setUpgradeModalData(null)} />
          <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔒</div>
            <h2 style={{ marginBottom: '8px', fontSize: '22px', fontWeight: '700' }}>
              Weekly Search Limit Reached
            </h2>
            <p style={{ marginBottom: '20px', color: '#888', fontSize: '14px' }}>
              You've used {upgradeModalData.searches_used}/{upgradeModalData.searches_limit} free searches this week.
              Resets {new Date(upgradeModalData.resets_at).toLocaleDateString()}.
            </p>
            <div style={{ textAlign: 'left', background: '#1a1a1a', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              {['Unlimited searches', '3% cashback on visits', 'Gift card redemptions', 'Spin the Wheel rewards'].map((perk) => (
                <div key={perk} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', color: '#ddd', fontSize: '14px' }}>
                  <span style={{ color: '#4caf50', fontWeight: '700' }}>✓</span>
                  {perk}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1, padding: '12px' }}
                onClick={() => setUpgradeModalData(null)}
              >
                Not now
              </button>
              <button
                className="btn btn-primary"
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  fontWeight: '700',
                }}
                onClick={() => navigate('/subscription')}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
}
